import { Toast } from '../../../components/Common/Toast';
import { HTTPStatusEnum } from '../../../interfaces/log.interface';
import { callAPI } from '../../../services/api.service';
import { TeacherOnboardingModel } from '../../../interfaces/teacher.interface';
import { AppDispatch } from '../../../store/store';
import { setTeacherOnboarding } from '../../../store/userSlice';
import { Dictionary } from '../../../interfaces/_common.interface';
import { SelectedDateTime } from '../components/PublishingTimelineForm';
import { ArchiveSelectorOption } from '../components/ProjectSelector';
import { localDateToUTCKeepSame } from '../../../helper/helper';
import { DateSelectorMode } from '../components/NewDatePickerPopup';

export interface SchoolSearchResult {
  id: string;
  name: string;
  city?: string;
  state: string;
  address?: string;
  zip?: string;
  districtName?: string;
  districtId?: string;
  districtExternalId?: string;
  schoolType?: string;
  externalId?: string;
}

export const searchSchools = async (
  query: string,
  state?: string,
  country?: string,
): Promise<SchoolSearchResult[]> => {
  if (!query || query.length < 2) return [];
  try {
    const params = new URLSearchParams({ q: query });
    if (state) params.set('state', state);
    if (country) params.set('country', country);
    const response = await callAPI(`api/schools/search?${params.toString()}`, null, 'GET');
    if (response?.status === HTTPStatusEnum.OK && Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  } catch {
    return [];
  }
};

export interface CreateSchoolPayload {
  name: string;
  state?: string;
  country: string;
  districtName?: string;
  city?: string;
}

export const checkSchoolsAvailable = async (country: string): Promise<boolean> => {
  try {
    const response = await callAPI(`api/schools/has-any?country=${encodeURIComponent(country)}`, null, 'GET');
    return response?.data?.hasAny === true;
  } catch {
    return false;
  }
};

export const createSchool = async (
  payload: CreateSchoolPayload,
): Promise<{ id: string; name: string } | null> => {
  try {
    const response = await callAPI('api/schools', payload, 'POST');
    if (response?.status === HTTPStatusEnum.OK && response?.data?.id) {
      return { id: response.data.id, name: response.data.name ?? payload.name };
    }
    Toast(response?.message || response?.error || 'Failed to add school', true);
    return null;
  } catch {
    Toast('Failed to add school', true);
    return null;
  }
};

export const createUserClassroom = async (
  name: string,
  success: (response: any) => void,
  options?: { projectName?: string; projectTemplateId?: string; startDate?: string },
): Promise<void> => {
  try {
    const body: Record<string, any> = { name };
    if (options?.projectName) body.projectName = options.projectName;
    if (options?.projectTemplateId) body.projectTemplateId = options.projectTemplateId;
    if (options?.startDate) body.startDate = options.startDate;

    const response = await callAPI('api/classrooms', body, 'POST');
    Toast(response?.message || response?.error || 'Unexpected Error Occurred', response?.status !== HTTPStatusEnum.OK);
    if (response?.status === HTTPStatusEnum.OK) {
      success(response);
    }
  } catch (error) {
    Toast('Failed to create classroom', true);
  }
};

export const createUserClassroomProject = async (
  classroomId: string | undefined,
  name: string,
  success: (response: any) => void,
  options?: { projectTemplateId?: string; startDate?: string },
): Promise<void> => {
  if (!classroomId) {
    return;
  }
  try {
    const body: Record<string, any> = { name };
    if (options?.projectTemplateId) body.projectTemplateId = options.projectTemplateId;
    if (options?.startDate) body.startDate = options.startDate;

    const response = await callAPI(`api/classrooms/${classroomId}/projects`, body, 'POST');
    if (response?.status === HTTPStatusEnum.OK) {
      success(response);
      Toast('Project successfully created', false);
    } else {
      Toast(response?.message || response?.error || 'Unexpected Error Occurred', true);
    }
  } catch (error) {
    Toast('Failed to create classroom project', true);
  }
};

export const saveTeacherSurvey = async (
  surveyData: TeacherOnboardingModel | null,
  success: () => void,
  ignoreNotification?: boolean,
  completeOnboarding?: boolean,
): Promise<boolean> => {
  if (!surveyData) {
    return false;
  }
  try {
    const response = await callAPI(
      'api/surveys',
      {
        data: {
          ...surveyData,
        },
        ...(completeOnboarding ? { completeOnboarding: true } : {}),
      },
      'POST',
    );
    if (ignoreNotification) {
      return response?.status === HTTPStatusEnum.OK;
    }
    if (response?.status === HTTPStatusEnum.OK) {
      Toast(response?.message || 'Successfully saved. Thank you.', false);
      success();
      return true;
    }
    Toast(response?.message || response?.error || 'Unexpected Error Occurred', true);
    return false;
  } catch (error) {
    Toast('Failed to saveSurvey', true);
    return false;
  }
};

export const saveTeacherFinalSurvey = async (
  surveyData: TeacherOnboardingModel | null,
  success: () => void,
  ignoreNotification?: boolean,
): Promise<void> => {
  if (!surveyData) {
    return;
  }
  try {
    const response = await callAPI(
      'api/surveys/final-survey',
      {
        data: {
          ...surveyData,
        },
      },
      'POST',
    );
    if (ignoreNotification) {
      return;
    }
    if (response?.status === HTTPStatusEnum.OK) {
      Toast(response?.message || 'Successfully saved. Thank you.', false);
      success();
    } else {
      Toast(response?.message || response?.error || 'Unexpected Error Occurred', true);
    }
  } catch (error) {
    Toast('Failed to saveSurvey', true);
  }
};

export enum UpdateProjectTimelineSourceEnum {
  CREATING_FLOW = 'creating_flow',
  SETTINGS = 'settings',
}

export const saveProjectTimeline = async (
  classroomId: string | undefined,
  projectId: string | undefined,
  publishingData: SelectedDateTime | null,
  mode: DateSelectorMode,
  success: () => void,
  source: UpdateProjectTimelineSourceEnum = UpdateProjectTimelineSourceEnum.CREATING_FLOW,
): Promise<void> => {
  if (!publishingData || !classroomId || !projectId) {
    return;
  }
  try {
    const response = await callAPI(
      `api/classrooms/${classroomId}/projects/${projectId}/survey-data`,
      {
        source,
        startDate: publishingData.startDate ? localDateToUTCKeepSame(publishingData.startDate) : null,
        startDateTime: publishingData.startDateTime,
        mode,
      },
      'PUT',
    );
    Toast(response?.message || response?.error || 'Unexpected Error Occurred', response?.status !== HTTPStatusEnum.OK);
    if (response?.status === HTTPStatusEnum.OK) {
      success();
    }
  } catch (error) {
    Toast('Failed to save your publishing celebration', true);
  }
};

export const saveProjectTourProgress = async (
  classroomId: string | undefined,
  projectId: string | undefined,
  state: 'start' | 'finish',
  success: boolean,
): Promise<void> => {
  try {
    await callAPI(
      `api/classrooms/${classroomId}/projects/${projectId}/survey-data/tour`,
      {
        state,
        success,
      },
      'POST',
    );
  } catch (error) {
    // Toast('Failed to save your publishing celebration', true);
  }
};

export const getOnboardingData = async (dispatch: AppDispatch) => {
  try {
    const response = await callAPI('api/surveys', null, 'GET');
    if (response?.status === HTTPStatusEnum.OK) {
      dispatch(setTeacherOnboarding({ data: response.data[0] || {} }));
    } else {
      dispatch(setTeacherOnboarding({ data: {} }));
    }
  } catch (error) {
    dispatch(setTeacherOnboarding({ data: {} }));
  }
};

export const fetchClassroomBooks = async (
  classroomId: string | undefined,
  archivedBooks: string,
  search: string | undefined,
  projectId: string | null | undefined,
  setLoading: Function,
  setBooks: Function,
  setTotalPages: Function,
): Promise<void> => {
  if (!classroomId) {
    return;
  }
  try {
    setLoading(true);
    const queryParams: Dictionary<string> = {
      ...(search ? { search } : {}),
      ...(projectId && projectId !== ArchiveSelectorOption.Archive ? { projectId } : {}),
      archived: archivedBooks,
    };
    const query = Object.keys(queryParams)
      .map((key) => `${key}=${queryParams[key]}`)
      .join('&');
    const response = await callAPI(`api/teacher/books/classroom/${classroomId}?${query}`);

    if (response?.status === HTTPStatusEnum.OK) {
      setBooks(response?.books);
      setTotalPages(response?.totalPages);
    } else {
      Toast(response?.message || response?.error);
    }
  } catch (error) {
    Toast('Error fetching books', true);
  } finally {
    setLoading(false);
  }
};

/**
 * Set a project as the default for a classroom
 * @param classroomId Classroom ID
 * @param projectId Project ID to set as default
 * @param success Callback function on success
 */
export const setDefaultProject = async (
  classroomId: string | undefined,
  projectId: string | undefined,
  success: () => void,
): Promise<void> => {
  if (!classroomId || !projectId) {
    return;
  }
  try {
    const response = await callAPI(`api/classrooms/${classroomId}/projects/${projectId}/set-default`, {}, 'PUT');
    Toast(response?.message || response?.error || 'Unexpected Error Occurred', response?.status !== HTTPStatusEnum.OK);
    if (response?.status === HTTPStatusEnum.OK) {
      success();
    }
  } catch (error) {
    Toast('Failed to set default project', true);
  }
};

/**
 * Get the default project for a classroom
 * @param classroomId Classroom ID
 * @returns Default project or null if not found
 */
export const getDefaultProject = async (classroomId: string | undefined): Promise<any> => {
  if (!classroomId) {
    return null;
  }
  try {
    const response = await callAPI(`api/classrooms/${classroomId}/projects/default`, null, 'GET');
    if (response?.status === HTTPStatusEnum.OK) {
      return response.project;
    }
    return null;
  } catch (error) {
    console.error('Error getting default project:', error);
    return null;
  }
};
